import {
  PreFinalizeQuotationFedexApiDto,
  GetOneExpeditionBoResponseDto,
  PreFinalizeQuotationApiDto,
  PreFinalizeCustomQuotationFedexApiDto,
  PreFinalizeCustomQuotationApiDto,
  UpdateFinalizedQuotationDto,
  UpdateFinalizedCustomQuotationDto,
  UpdatePreFinalizeQuotationApiDto,
  UpdatePreFinalizeCustomQuotationApiDto,
  GetAllCompanyDto,
  PreFinalizeCustomQuotationSimplifiedApiDto,
} from "@/types/api/api-types";
import {
  QuotationUpdateBoFormData,
  CustomQuotationUpdateBoFormData,
  ExpeditionFedexApiData,
  ExpeditionWoApiData,
  UpdateFinalizedQuotationFormData,
  UpdateFinalizedCustomQuotationFormData,
  MANUFACTURER_COUNTRY_CODE,
  ManufacturerInfoData,
  isManufacturerInfoRequiredFor,
} from "@/types/forms/bo/expeditionBo.form";
import { CustomQuotationTypeEnum } from "@/constants/enums/customQuotationType.enum";
import { ParcelProductTypeEnum } from "@/constants/enums/parcelProductType.enum";
import { OtherCustomQuotationTypeEnum } from "@/constants/enums/otherCustomQuotationType.enum";
import { NoteType } from "@/constants/enums/noteType.enum";
import { FedexPackagingEnum } from "@/constants/enums/fedexPackaging.enum";
import { FedexServiceEnum } from "@/constants/enums/fedexService.enum";
import { FedexSignatureEnum } from "@/constants/enums/fedexSignature.enum";
import { WineColorEnum } from "@/constants/enums/wineColor.enum";
import { format } from "date-fns";
import { PELICAN_AIR_SERVICES_SHIPPER_INFO } from "@/constants/pelicanAirServices";
import { FinalizeApi, FinalizeApiEnum } from "@/types/finalizeApi.type";
import { DeliveryContentData } from "@/types/forms/bo/expeditionBo.form";
import {
  getDefaultDeliveryContentRow,
  getDestinationRegion,
} from "./shared/sections/deliveryContent/deliveryContent.utils";
import { accentsTidy } from "@/lib/utils";

// Width printed in the reference area of the FedEx label; anything beyond is hidden.
const SHIPPING_REFERENCE_MAX_LENGTH = 29;

type MappedDeliveryContent = {
  wineName: string;
  year: number | undefined;
  alcoholPercent: number;
  volume: number;
  color: WineColorEnum;
  numberOfBottles: number;
  unitPrice: number;
  productId: string | undefined;
  productUSAId: string | undefined;
};

export function mapDeliveryContent(deliveryContent: DeliveryContentData[]): MappedDeliveryContent[];
export function mapDeliveryContent(
  deliveryContent: DeliveryContentData[] | undefined,
): MappedDeliveryContent[] | undefined;
export function mapDeliveryContent(
  deliveryContent?: DeliveryContentData[],
): MappedDeliveryContent[] | undefined {
  return deliveryContent?.map((content) => ({
    wineName: content.wineName,
    year: isNaN(Number(content.year)) ? undefined : Number(content.year),
    alcoholPercent: content.alcoholPercent,
    volume: Number(content.volume),
    color: content.color as WineColorEnum,
    numberOfBottles: content.numberOfBottles,
    unitPrice: content.unitPrice,
    productId: content.productId || undefined,
    productUSAId: content.productUSAId || undefined,
  }));
}

export const buildShippingReference = ({
  expeditionNumber,
  companyName,
  totalBottleCount,
  woodbox,
  isPriority,
  isReExpedition,
}: {
  expeditionNumber: string;
  companyName: string;
  totalBottleCount: number;
  woodbox: boolean;
  isPriority: boolean;
  isReExpedition: boolean;
}): string => {
  const capped = Math.min(999, Math.max(0, Math.floor(Number(totalBottleCount) || 0)));
  const bottles = String(capped).padStart(3, "0");
  const head = [isReExpedition && "RXP", isPriority && "PRIO", expeditionNumber]
    .filter(Boolean)
    .join(" ");
  const tail = woodbox ? `CB ${bottles}` : bottles;
  const separatorsLength = head ? 2 : 1;
  const maxCompanyNameLength = Math.max(
    0,
    SHIPPING_REFERENCE_MAX_LENGTH - head.length - tail.length - separatorsLength,
  );
  return [head, tail, companyName.slice(0, maxCompanyNameLength).trim()].filter(Boolean).join(" ");
};

const getManufacturerInfoDefaultValues = (
  expedition: GetOneExpeditionBoResponseDto,
  company: GetAllCompanyDto,
): ManufacturerInfoData | undefined => {
  const billingEntity = company.billingEntities.find(
    (billingEntity) => billingEntity.id === expedition?.expeditionBillingDetail?.billingEntityId,
  );
  const manufacturerInfo = expedition?.manufacturerInfo;
  if (
    !isManufacturerInfoRequiredFor(billingEntity?.country, expedition?.deliveryInfo?.country) ||
    manufacturerInfo?.countryCode !== MANUFACTURER_COUNTRY_CODE
  ) {
    return undefined;
  }

  return {
    companyName: manufacturerInfo.companyName || "",
    streetLines: [manufacturerInfo.streetLines[0] || "", manufacturerInfo.streetLines[1] || ""],
    city: manufacturerInfo.city || "",
    postalCode: manufacturerInfo.postalCode || "",
    countryCode: MANUFACTURER_COUNTRY_CODE,
  };
};

const mapManufacturerInfo = (
  manufacturerInfo: ManufacturerInfoData | undefined,
): PreFinalizeQuotationApiDto["manufacturerInfo"] =>
  manufacturerInfo
    ? {
        companyName: manufacturerInfo.companyName,
        streetLines: manufacturerInfo.streetLines.filter((line) => line.length > 0),
        city: manufacturerInfo.city,
        postalCode: manufacturerInfo.postalCode,
        countryCode: manufacturerInfo.countryCode,
      }
    : undefined;

export const QuotationUpdateBoDefaultValues = ({
  expedition,
  company,
  apiEnum,
}: {
  expedition: GetOneExpeditionBoResponseDto;
  company: GetAllCompanyDto;
  apiEnum?: FinalizeApi;
}): QuotationUpdateBoFormData => {
  const hasDeliveryContent =
    expedition?.quotation?.deliveryContent && expedition?.quotation?.deliveryContent.length > 0;

  const isRemote =
    !expedition?.pickupInfo?.pickupLocation?.pickupZones ||
    expedition?.pickupInfo?.pickupLocation?.pickupZones?.remote ||
    false;

  const expeditionNumber =
    expedition?.quotation?.identifier || expedition?.customQuotation?.identifier;

  const destinationRegion = getDestinationRegion(expedition?.deliveryInfo?.country);
  const viaImportedInvoice = expedition?.quotation?.viaImportedInvoice ?? false;

  const billingEntity = company.billingEntities.find(
    (billingEntity) => billingEntity.id === expedition?.expeditionBillingDetail?.billingEntityId,
  );

  const deliveryContent: QuotationUpdateBoFormData["deliveryContent"] = hasDeliveryContent
    ? expedition.quotation!.deliveryContent!.map((product) => ({
        wineName: product.wineName,
        year: product.year ? product.year.toString() : undefined,
        alcoholPercent: product.alcoholPercent,
        volume: product.volume,
        color: product.color as WineColorEnum,
        numberOfBottles: product.numberOfBottles,
        unitPrice: product.unitPrice,
        productId: destinationRegion === "UE" ? "-" : product.productId || undefined,
        productUSAId: product.productUSAId || undefined,
      }))
    : expedition.quotation!.quotationProducts!.map((product) =>
        getDefaultDeliveryContentRow(
          destinationRegion,
          {
            volume: product.volume,
            numberOfBottles: product.bottleQuantity,
            unitPrice: parseFloat((product.value / product.bottleQuantity).toFixed(4)),
          },
          viaImportedInvoice,
        ),
      );

  const expeditionFedexApi: ExpeditionFedexApiData = expedition?.expeditionFedexApi
    ? ({
        ...expedition?.expeditionFedexApi,
        deliveryInstructions:
          expedition?.expeditionFedexApi.deliveryInstructions || (undefined as unknown as string),
        shipDate: expedition?.expeditionFedexApi.shipDate
          ? new Date(expedition?.expeditionFedexApi.shipDate)
          : undefined,
      } as unknown as ExpeditionFedexApiData)
    : {
        deliveryInstructions: undefined as unknown as string,
        serviceType: undefined as unknown as FedexServiceEnum,
        packagingType: "YOUR_PACKAGING" as FedexPackagingEnum,
        signatureType: "NONE" as FedexSignatureEnum,
        fedexAccountId: undefined as unknown as string,
        expeditionPackaging: [
          {
            count: undefined as unknown as number,
            weight: undefined as unknown as number,
            length: undefined as unknown as number,
            width: undefined as unknown as number,
            height: undefined as unknown as number,
            value: expedition.expeditionOptions?.insurance ? (undefined as unknown as number) : 0,
          },
        ],
        shipDate: new Date(),
        shippingReference: buildShippingReference({
          expeditionNumber: expeditionNumber || "",
          companyName: billingEntity?.internalName || company.businessName,
          totalBottleCount: deliveryContent.reduce(
            (acc, curr) => acc + Number(curr.numberOfBottles || 0),
            0,
          ),
          woodbox:
            (expedition?.quotation &&
              expedition?.quotation?.quotationWoodBox &&
              expedition?.quotation?.quotationWoodBox?.length > 0) ||
            false,
          isPriority: expedition?.expeditionOptions?.express || false,
          isReExpedition: !!expedition?.basedOnId,
        }),
      };

  const expeditionWoApi: ExpeditionWoApiData = expedition?.expeditionWoApi
    ? ({
        ...expedition?.expeditionWoApi,
      } as unknown as ExpeditionWoApiData)
    : {
        packageCount: undefined as unknown as number,
        weight: undefined as unknown as number,
      };

  const shipperInfo: PreFinalizeQuotationFedexApiDto["shipperInfo"] = expedition?.shipperInfo
    ? {
        contactName: expedition?.shipperInfo.contactName || (undefined as unknown as string),
        companyName: expedition?.shipperInfo.companyName || (undefined as unknown as string),
        streetLines: expedition?.shipperInfo.streetLines || ["", ""],
        city: expedition?.shipperInfo.city || (undefined as unknown as string),
        stateOrProvinceCode:
          expedition?.shipperInfo.stateOrProvinceCode || (undefined as unknown as string),
        postalCode: expedition?.shipperInfo.postalCode || (undefined as unknown as string),
        countryCode: expedition?.shipperInfo.countryCode || (undefined as unknown as string),
        email: expedition?.shipperInfo.email || (undefined as unknown as string),
        phone: expedition?.shipperInfo.phone || (undefined as unknown as string),
        residential: expedition?.shipperInfo.residential || (undefined as unknown as boolean),
        taxId: expedition?.shipperInfo.taxId || (undefined as unknown as string),
      }
    : { ...PELICAN_AIR_SERVICES_SHIPPER_INFO };

  const apiEumValue = apiEnum
    ? apiEnum === FinalizeApiEnum.NONE_SIMPLIFIED
      ? "NONE"
      : apiEnum
    : expedition.expeditionFedexApi && expedition.expeditionFedexApi !== null
      ? "FEDEX"
      : "NONE";

  const hydratedDaeNumbers = (expedition?.expeditionOptions?.daeNumbers ?? []).filter(
    (n): n is string => typeof n === "string",
  );
  const hydratedMrnNumbers = (expedition?.expeditionOptions?.mrnNumbers ?? []).filter(
    (n): n is string => typeof n === "string",
  );

  return {
    deliveryInfo: {
      company: expedition?.deliveryInfo?.company || undefined,
      lastName: expedition?.deliveryInfo?.lastName || (undefined as unknown as string),
      firstName: expedition?.deliveryInfo?.firstName || (undefined as unknown as string),
      email: expedition?.deliveryInfo?.email || (undefined as unknown as string),
      phone: expedition?.deliveryInfo?.phone || (undefined as unknown as string),
      street: expedition?.deliveryInfo?.street || (undefined as unknown as string),
      additionalInfo: expedition?.deliveryInfo?.additionalInfo || undefined,
      additionalInfo2: expedition?.deliveryInfo?.additionalInfo2 || undefined,
      postalCode: expedition?.deliveryInfo?.postalCode || (undefined as unknown as string),
      city: expedition?.deliveryInfo?.city || (undefined as unknown as string),
      countryCode: expedition?.deliveryInfo?.country || (undefined as unknown as string),
      stateOrProvinceCode: expedition?.deliveryInfo?.state || undefined,
      additionalEmail: expedition?.deliveryInfo?.additionalEmail || "",
      additionalPhone: expedition?.deliveryInfo?.additionalPhone || "",
      clientAdditionalInfo: expedition?.deliveryInfo?.clientAdditionalInfo || [],
    },
    shipperInfo,
    manufacturerInfo: getManufacturerInfoDefaultValues(expedition, company),
    customId: expedition?.quotation?.customId || (undefined as unknown as string),
    deliveryContent,
    updatePriceParameters: false,
    quotationWoodBox:
      expedition?.quotation?.quotationWoodBox?.map((woodBox) => ({
        bottleQuantity: woodBox.bottleQuantity,
        boxQuantity: woodBox.boxQuantity,
        bottleVolume: woodBox.bottleVolume,
      })) || [],
    billingEntityId:
      expedition.expeditionBillingDetail?.billingEntityId || (undefined as unknown as string),
    pickUpLocationId: expedition.pickupInfo?.pickupLocation?.id || (undefined as unknown as string),
    comment: expedition?.deliveryInfo?.comment || undefined,
    options: {
      insurance: expedition?.expeditionOptions?.insurance || false,
      dapOption: expedition?.expeditionOptions?.dapOption || false,
      express: expedition?.expeditionOptions?.express || false,
      sample: expedition?.expeditionOptions?.sample || false,
      dae: expedition?.expeditionOptions?.dae || false,
      mrn: expedition?.expeditionOptions?.mrn || false,
      signature: expedition?.expeditionOptions?.signature || false,
      packagingPriority: expedition?.expeditionOptions?.packagingPriority || false,
      remotePickup: expedition?.expeditionOptions?.remotePickup || isRemote,
      includeCompanyName: expedition?.expeditionOptions?.includeCompanyName || false,
      handDelivery: expedition?.expeditionOptions?.handDelivery || false,
      sampleText: expedition?.expeditionOptions?.sampleText || "Samples Only - No commercial value",
      daeNumbers: hydratedDaeNumbers,
      mrnNumbers: hydratedMrnNumbers,
      personalUse: expedition?.expeditionOptions?.personalUse || "",
      presonalUseToggle: expedition?.expeditionOptions?.personalUse ? true : false,
    },
    reference: expedition?.expeditionBillingDetail?.reference || undefined,
    expeditionFedexApi:
      apiEumValue === "FEDEX"
        ? expeditionFedexApi
        : (undefined as unknown as ExpeditionFedexApiData),
    expeditionWoApi:
      apiEumValue === "NONE" ? expeditionWoApi : (undefined as unknown as ExpeditionWoApiData),
    apiEnum: apiEumValue,
    importerId: expedition?.importerId || company.importerId || undefined,
  };
};

/** Custom quotations have no imported invoice, but their delivery content follows the imported-invoice rules. */
export const CUSTOM_QUOTATION_VIA_IMPORTED_INVOICE = true;

export const CustomQuotationUpdateBoDefaultValues = ({
  expedition,
  company,
  apiEnum,
}: {
  expedition: GetOneExpeditionBoResponseDto;
  company: GetAllCompanyDto;
  apiEnum?: FinalizeApi;
}): CustomQuotationUpdateBoFormData => {
  const isRemote =
    !expedition?.pickupInfo?.pickupLocation?.pickupZones ||
    expedition?.pickupInfo?.pickupLocation?.pickupZones?.remote ||
    false;

  const expeditionNumber =
    expedition?.quotation?.identifier || expedition?.customQuotation?.identifier;

  const hasDeliveryContent =
    expedition.customQuotation?.quotationDeliveryContent &&
    expedition.customQuotation.quotationDeliveryContent.length > 0;

  const customQuotationType = expedition.customQuotation?.type as CustomQuotationTypeEnum;

  const canUseCustomQuotationDefaults =
    !hasDeliveryContent &&
    (customQuotationType === CustomQuotationTypeEnum.PARCEL ||
      customQuotationType === CustomQuotationTypeEnum.PALETTE);

  const parcelProducts = expedition.customQuotation?.parcelDetail?.parcelProducts ?? [];
  const paletteProducts = expedition.customQuotation?.paletteDetail?.paletteProducts ?? [];
  const parcelPackages = expedition.customQuotation?.parcelDetail?.parcelPackages ?? [];
  const palettePackages = expedition.customQuotation?.paletteDetail?.palettePackages ?? [];

  const insuranceOption = expedition?.expeditionOptions?.insurance;

  const customQuotationPackages =
    customQuotationType === CustomQuotationTypeEnum.PARCEL
      ? parcelPackages
      : customQuotationType === CustomQuotationTypeEnum.PALETTE
        ? palettePackages
        : undefined;

  const apiEnumValue: FinalizeApi = apiEnum
    ? apiEnum
    : expedition.expeditionFedexApi && expedition.expeditionFedexApi !== null
      ? "FEDEX"
      : "NONE";

  const hydratedDaeNumbers = (expedition?.expeditionOptions?.daeNumbers ?? []).filter(
    (n): n is string => typeof n === "string",
  );
  const hydratedMrnNumbers = (expedition?.expeditionOptions?.mrnNumbers ?? []).filter(
    (n): n is string => typeof n === "string",
  );

  const destinationRegion = getDestinationRegion(expedition?.deliveryInfo?.country);
  const viaImportedInvoice = CUSTOM_QUOTATION_VIA_IMPORTED_INVOICE;
  const emptyDeliveryContentRow = getDefaultDeliveryContentRow(
    destinationRegion,
    {
      volume: undefined as unknown as number,
      numberOfBottles: undefined as unknown as number,
      unitPrice: undefined as unknown as number,
    },
    viaImportedInvoice,
  );

  const isUeSimplified = destinationRegion === "UE" && !!viaImportedInvoice;

  const billingEntity = company.billingEntities.find(
    (billingEntity) => billingEntity.id === expedition?.expeditionBillingDetail?.billingEntityId,
  );

  const deliveryContent: DeliveryContentData[] = hasDeliveryContent
    ? expedition.customQuotation!.quotationDeliveryContent!.map((product) =>
        getDefaultDeliveryContentRow(
          destinationRegion,
          {
            ...(product.wineName != null && { wineName: product.wineName }),
            year: product.year ? product.year.toString() : undefined,
            ...(product.alcoholPercent != null && { alcoholPercent: product.alcoholPercent }),
            ...(product.color != null && { color: product.color as WineColorEnum }),
            volume: product.volume,
            numberOfBottles: product.numberOfBottles,
            unitPrice: product.unitPrice,
            productId: destinationRegion === "UE" ? "-" : product.productId || undefined,
            productUSAId: product.productUSAId || undefined,
          },
          viaImportedInvoice,
        ),
      )
    : canUseCustomQuotationDefaults &&
        customQuotationType === CustomQuotationTypeEnum.PARCEL &&
        parcelProducts.length > 0
      ? parcelProducts.map((product) =>
          getDefaultDeliveryContentRow(
            destinationRegion,
            {
              ...(!isUeSimplified && product.type === ParcelProductTypeEnum.OTHER
                ? {
                    wineName: product.description || (undefined as unknown as string),
                  }
                : {}),
              volume: product.volume ?? (undefined as unknown as number),
              numberOfBottles: product.bottleQuantity ?? (undefined as unknown as number),
              unitPrice: undefined as unknown as number,
            },
            viaImportedInvoice,
          ),
        )
      : canUseCustomQuotationDefaults &&
          customQuotationType === CustomQuotationTypeEnum.PALETTE &&
          paletteProducts.length > 0
        ? paletteProducts.map((product) =>
            getDefaultDeliveryContentRow(
              destinationRegion,
              {
                volume: product.volume,
                numberOfBottles: product.bottleQuantity,
                unitPrice: undefined as unknown as number,
              },
              viaImportedInvoice,
            ),
          )
        : [emptyDeliveryContentRow];

  const defaultExpeditionPackagingFormCustomQuotation =
    canUseCustomQuotationDefaults && customQuotationPackages && customQuotationPackages.length > 0
      ? customQuotationPackages.map((pkg) => ({
          count: 1,
          weight: pkg.weight,
          length: pkg.length,
          width: pkg.width,
          height: pkg.height,
          value: insuranceOption ? pkg.value : 0,
        }))
      : [
          {
            count: undefined as unknown as number,
            weight: undefined as unknown as number,
            length: undefined as unknown as number,
            width: undefined as unknown as number,
            height: undefined as unknown as number,
            value: expedition.expeditionOptions?.insurance ? (undefined as unknown as number) : 0,
          },
        ];

  const expeditionFedexApi: ExpeditionFedexApiData = expedition?.expeditionFedexApi
    ? ({
        ...expedition?.expeditionFedexApi,
        deliveryInstructions:
          expedition?.expeditionFedexApi.deliveryInstructions || (undefined as unknown as string),
        shipDate: expedition?.expeditionFedexApi.shipDate
          ? new Date(expedition?.expeditionFedexApi.shipDate)
          : undefined,
      } as unknown as ExpeditionFedexApiData)
    : {
        deliveryInstructions: undefined as unknown as string,
        serviceType: undefined as unknown as FedexServiceEnum,
        packagingType: "YOUR_PACKAGING" as FedexPackagingEnum,
        signatureType: "NONE" as FedexSignatureEnum,
        fedexAccountId: undefined as unknown as string,
        expeditionPackaging: defaultExpeditionPackagingFormCustomQuotation,
        shipDate: new Date(),
        shippingReference: buildShippingReference({
          expeditionNumber: expeditionNumber || "",
          companyName: billingEntity?.internalName || company.businessName,
          totalBottleCount: deliveryContent.reduce(
            (acc, curr) => acc + Number(curr.numberOfBottles || 0),
            0,
          ),
          woodbox:
            (expedition?.quotation &&
              expedition?.quotation?.quotationWoodBox &&
              expedition?.quotation?.quotationWoodBox?.length > 0) ||
            false,
          isPriority: expedition?.expeditionOptions?.express || false,
          isReExpedition: !!expedition?.basedOnId,
        }),
      };

  const expeditionWoApi: ExpeditionWoApiData = expedition?.expeditionWoApi
    ? ({
        ...expedition?.expeditionWoApi,
      } as unknown as ExpeditionWoApiData)
    : canUseCustomQuotationDefaults && customQuotationPackages && customQuotationPackages.length > 0
      ? {
          packageCount: customQuotationPackages.length as unknown as number,
          weight: customQuotationPackages.reduce(
            (sum, pkg) => sum + pkg.weight,
            0,
          ) as unknown as number,
        }
      : {
          packageCount: undefined as unknown as number,
          weight: undefined as unknown as number,
        };

  const shipperInfo: PreFinalizeCustomQuotationFedexApiDto["shipperInfo"] = expedition?.shipperInfo
    ? {
        contactName: expedition?.shipperInfo.contactName || (undefined as unknown as string),
        companyName: expedition?.shipperInfo.companyName || (undefined as unknown as string),
        streetLines: expedition?.shipperInfo.streetLines || ["", ""],
        city: expedition?.shipperInfo.city || (undefined as unknown as string),
        stateOrProvinceCode:
          expedition?.shipperInfo.stateOrProvinceCode || (undefined as unknown as string),
        postalCode: expedition?.shipperInfo.postalCode || (undefined as unknown as string),
        countryCode: expedition?.shipperInfo.countryCode || (undefined as unknown as string),
        email: expedition?.shipperInfo.email || (undefined as unknown as string),
        phone: expedition?.shipperInfo.phone || (undefined as unknown as string),
        residential: expedition?.shipperInfo.residential || (undefined as unknown as boolean),
        taxId: expedition?.shipperInfo.taxId || (undefined as unknown as string),
      }
    : { ...PELICAN_AIR_SERVICES_SHIPPER_INFO };

  const parcelDetail = expedition.customQuotation?.parcelDetail
    ? {
        parcelCount: expedition.customQuotation.parcelDetail.parcelPackages?.length ?? 1,
        parcelPackages: expedition.customQuotation.parcelDetail.parcelPackages.map((pkg) => ({
          weight: pkg.weight,
          length: pkg.length,
          width: pkg.width,
          height: pkg.height,
          value: pkg.value,
        })),
        parcelProducts: expedition.customQuotation.parcelDetail.parcelProducts.map((product) => ({
          type: product.type as ParcelProductTypeEnum,
          description: product.description || undefined,
          volume: product.volume ?? undefined,
          bottleQuantity: product.bottleQuantity ?? undefined,
        })),
      }
    : undefined;

  const paletteDetail = expedition.customQuotation?.paletteDetail
    ? {
        palettePackages: expedition.customQuotation.paletteDetail.palettePackages.map((pkg) => ({
          length: pkg.length,
          width: pkg.width,
          height: pkg.height,
          weight: pkg.weight,
          value: pkg.value,
        })),
        paletteProducts: expedition.customQuotation.paletteDetail.paletteProducts.map(
          (product) => ({
            volume: product.volume,
            bottleQuantity: product.bottleQuantity,
          }),
        ),
      }
    : undefined;

  const otherDetail = expedition.customQuotation?.otherDetail
    ? {
        value:
          "value" in expedition.customQuotation.otherDetail &&
          typeof expedition.customQuotation.otherDetail.value === "number"
            ? expedition.customQuotation.otherDetail.value
            : 0,
        numberOfExpedition: expedition.customQuotation.otherDetail.numberOfExpedition,
        isMultipleExpedition: expedition.customQuotation.otherDetail.isMultipleExpedition,
        otherCustomQuotationType: expedition.customQuotation.otherDetail
          .otherCustomQuotationType as OtherCustomQuotationTypeEnum,
      }
    : undefined;

  const defaultValues = {
    deliveryInfo: {
      company: expedition?.deliveryInfo?.company || undefined,
      lastName: expedition?.deliveryInfo?.lastName || (undefined as unknown as string),
      firstName: expedition?.deliveryInfo?.firstName || (undefined as unknown as string),
      email: expedition?.deliveryInfo?.email || (undefined as unknown as string),
      phone: expedition?.deliveryInfo?.phone || (undefined as unknown as string),
      street: expedition?.deliveryInfo?.street || (undefined as unknown as string),
      additionalInfo: expedition?.deliveryInfo?.additionalInfo || undefined,
      additionalInfo2: expedition?.deliveryInfo?.additionalInfo2 || undefined,
      postalCode: expedition?.deliveryInfo?.postalCode || (undefined as unknown as string),
      city: expedition?.deliveryInfo?.city || (undefined as unknown as string),
      countryCode: expedition?.deliveryInfo?.country || (undefined as unknown as string),
      stateOrProvinceCode: expedition?.deliveryInfo?.state || undefined,
      additionalEmail: expedition?.deliveryInfo?.additionalEmail || "",
      additionalPhone: expedition?.deliveryInfo?.additionalPhone || "",
      clientAdditionalInfo: expedition?.deliveryInfo?.clientAdditionalInfo || [],
    },
    shipperInfo,
    manufacturerInfo: getManufacturerInfoDefaultValues(expedition, company),
    deliveryContent,
    customId: expedition?.customQuotation?.customId || "",
    billingEntityId:
      expedition.expeditionBillingDetail?.billingEntityId || (undefined as unknown as string),
    pickUpLocationId: expedition.pickupInfo?.pickupLocation?.id || (undefined as unknown as string),
    comment: expedition?.deliveryInfo?.comment || undefined,
    options: {
      insurance: expedition?.expeditionOptions?.insurance || false,
      dapOption: expedition?.expeditionOptions?.dapOption || false,
      express: expedition?.expeditionOptions?.express || false,
      sample: expedition?.expeditionOptions?.sample || false,
      dae: expedition?.expeditionOptions?.dae || false,
      mrn: expedition?.expeditionOptions?.mrn || false,
      signature: expedition?.expeditionOptions?.signature || false,
      packagingPriority: expedition?.expeditionOptions?.packagingPriority || false,
      remotePickup: expedition?.expeditionOptions?.remotePickup || isRemote,
      includeCompanyName: expedition?.expeditionOptions?.includeCompanyName || false,
      handDelivery: expedition?.expeditionOptions?.handDelivery || false,
      sampleText: expedition?.expeditionOptions?.sampleText || "Samples Only - No commercial value",
      daeNumbers: hydratedDaeNumbers,
      mrnNumbers: hydratedMrnNumbers,
      personalUse: expedition?.expeditionOptions?.personalUse || "",
      presonalUseToggle: expedition?.expeditionOptions?.personalUse ? true : false,
    },
    reference: expedition?.expeditionBillingDetail?.reference || undefined,
    type: customQuotationType,
    parcelDetail,
    paletteDetail,
    otherDetail,
    expeditionFedexApi:
      apiEnumValue === "FEDEX"
        ? expeditionFedexApi
        : (undefined as unknown as ExpeditionFedexApiData),
    expeditionWoApi:
      apiEnumValue === "NONE" ? expeditionWoApi : (undefined as unknown as ExpeditionWoApiData),
    apiEnum: FinalizeApiEnum[apiEnumValue as keyof typeof FinalizeApiEnum],
    importerId: expedition?.importerId || company.importerId || undefined,
  };

  return defaultValues;
};

const preFinalizeQuotationPayload = ({ data }: { data: QuotationUpdateBoFormData }) => {
  const deliveryContent: PreFinalizeQuotationFedexApiDto["deliveryContent"] =
    data.deliveryContent.map((content) => ({
      wineName: content.wineName,
      year: isNaN(Number(content.year)) ? undefined : Number(content.year),
      alcoholPercent: content.alcoholPercent,
      volume: content.volume,
      color: content.color as WineColorEnum,
      numberOfBottles: content.numberOfBottles,
      unitPrice: content.unitPrice,
      productId: content.productId || undefined,
      productUSAId: content.productUSAId || undefined,
    }));

  const shipperInfo: PreFinalizeQuotationFedexApiDto["shipperInfo"] = {
    contactName: accentsTidy(data.shipperInfo.contactName),
    companyName: data.shipperInfo.companyName
      ? accentsTidy(data.shipperInfo.companyName)
      : undefined,
    streetLines: data.shipperInfo.streetLines
      ? data.shipperInfo.streetLines.map((line) => accentsTidy(line))
      : [],
    city: accentsTidy(data.shipperInfo.city),
    stateOrProvinceCode: data.shipperInfo.stateOrProvinceCode,
    postalCode: data.shipperInfo.postalCode || "",
    countryCode: data.shipperInfo.countryCode,
    email: data.shipperInfo.email || "",
    phone: data.shipperInfo.phone,
    residential: data.shipperInfo.residential,
    taxId: data.shipperInfo.taxId,
  };

  const { presonalUseToggle: _presonalUseToggle, ...options } = data.options;

  return {
    deliveryInfo: {
      ...data.deliveryInfo,
      company: data.deliveryInfo.company ? accentsTidy(data.deliveryInfo.company) : undefined,
      lastName: accentsTidy(data.deliveryInfo.lastName),
      firstName: accentsTidy(data.deliveryInfo.firstName),
      street: accentsTidy(data.deliveryInfo.street),
      additionalInfo: data.deliveryInfo.additionalInfo
        ? accentsTidy(data.deliveryInfo.additionalInfo)
        : undefined,
      additionalInfo2: data.deliveryInfo.additionalInfo2
        ? accentsTidy(data.deliveryInfo.additionalInfo2)
        : undefined,
      city: accentsTidy(data.deliveryInfo.city),
      stateOrProvinceCode: data.deliveryInfo.stateOrProvinceCode,
      postalCode: data.deliveryInfo.postalCode || "",
    },
    shipperInfo,
    manufacturerInfo: mapManufacturerInfo(
      "manufacturerInfo" in data ? data.manufacturerInfo : undefined,
    ),
    customId: data.customId,
    deliveryContent,
    woodBoxes: data.quotationWoodBox,
    billingEntityId: data.billingEntityId,
    pickUpLocationId: data.pickUpLocationId,
    reference: data.reference ? accentsTidy(data.reference) : undefined,
    options: {
      ...options,
      remotePickup: shipperInfo.companyName === "Pelican Air Services" ? false : true,
      sampleText: data.options.sample ? data.options.sampleText : "",
      daeNumbers: data.options.dae ? data.options.daeNumbers : [],
      mrn: data.options.dae && data.options.mrn,
      mrnNumbers: data.options.dae && data.options.mrn ? data.options.mrnNumbers : [],
      handDelivery: data.apiEnum === "NONE" && data.options.handDelivery,
      personalUse: data.options.presonalUseToggle ? data.options.personalUse : "",
    },
    updatePriceParameters: data.updatePriceParameters,
    importerId: data.importerId || undefined,
  };
};

export const preFinalizeWoApiQuotationPayload = ({
  data,
}: {
  data: Extract<QuotationUpdateBoFormData, { apiEnum: "NONE" }>;
}): PreFinalizeQuotationApiDto => {
  const basePayload = preFinalizeQuotationPayload({ data });

  const expeditionWoApi: PreFinalizeQuotationApiDto["expeditionWoApi"] = {
    packageCount: data.expeditionWoApi.packageCount,
    weight: data.expeditionWoApi.weight,
  };

  return {
    ...basePayload,
    expeditionWoApi,
  };
};

const mapCustomQuotationTypeDetails = (
  data: CustomQuotationUpdateBoFormData,
): Pick<
  PreFinalizeCustomQuotationApiDto,
  "type" | "parcelDetail" | "paletteDetail" | "otherDetail"
> => {
  if (data.type === CustomQuotationTypeEnum.PARCEL && data.parcelDetail) {
    return {
      type: data.type,
      parcelDetail: {
        parcelCount: data.parcelDetail.parcelCount,
        parcelPackages: data.parcelDetail.parcelPackages.map((pkg) => ({
          weight: pkg.weight,
          length: pkg.length,
          width: pkg.width,
          height: pkg.height,
          value: pkg.value,
        })),
        parcelProducts: data.parcelDetail.parcelProducts.map((product) => ({
          type: product.type as ParcelProductTypeEnum,
          description: product.description ?? undefined,
          volume: product.volume ?? undefined,
          bottleQuantity: product.bottleQuantity ?? undefined,
        })),
      },
    };
  }

  if (data.type === CustomQuotationTypeEnum.PALETTE && data.paletteDetail) {
    return {
      type: data.type,
      paletteDetail: {
        palettePackages: data.paletteDetail.palettePackages.map((pkg) => ({
          length: pkg.length,
          width: pkg.width,
          height: pkg.height,
          weight: pkg.weight,
          value: pkg.value,
        })),
        paletteProducts: data.paletteDetail.paletteProducts.map((product) => ({
          volume: product.volume,
          bottleQuantity: product.bottleQuantity,
        })),
      },
    };
  }

  if (data.type === CustomQuotationTypeEnum.OTHER && data.otherDetail) {
    return {
      type: data.type,
      otherDetail: {
        value: data.otherDetail.value,
        numberOfExpedition: data.otherDetail.numberOfExpedition,
        isMultipleExpedition: data.otherDetail.isMultipleExpedition,
        otherCustomQuotationType: data.otherDetail
          .otherCustomQuotationType as OtherCustomQuotationTypeEnum,
      },
    };
  }

  return { type: data.type };
};

const preFinalizeCustomQuotationPayloadBase = ({
  data,
}: {
  data: CustomQuotationUpdateBoFormData;
}) => {
  const shipperInfo: PreFinalizeCustomQuotationApiDto["shipperInfo"] = {
    contactName: accentsTidy(data.shipperInfo.contactName),
    companyName: data.shipperInfo.companyName
      ? accentsTidy(data.shipperInfo.companyName)
      : undefined,
    streetLines: data.shipperInfo.streetLines
      ? data.shipperInfo.streetLines.map((line) => accentsTidy(line))
      : [],
    city: accentsTidy(data.shipperInfo.city),
    stateOrProvinceCode: data.shipperInfo.stateOrProvinceCode,
    postalCode: data.shipperInfo.postalCode || "",
    countryCode: data.shipperInfo.countryCode,
    email: data.shipperInfo.email || "",
    phone: data.shipperInfo.phone,
    residential: data.shipperInfo.residential,
    taxId: data.shipperInfo.taxId,
  };

  const { presonalUseToggle: _presonalUseToggle, ...options } = data.options;

  const typeDetails = mapCustomQuotationTypeDetails(data);

  return {
    deliveryInfo: {
      ...data.deliveryInfo,
      company: data.deliveryInfo.company ? accentsTidy(data.deliveryInfo.company) : undefined,
      lastName: accentsTidy(data.deliveryInfo.lastName),
      firstName: accentsTidy(data.deliveryInfo.firstName),
      street: accentsTidy(data.deliveryInfo.street),
      additionalInfo: data.deliveryInfo.additionalInfo
        ? accentsTidy(data.deliveryInfo.additionalInfo)
        : undefined,
      additionalInfo2: data.deliveryInfo.additionalInfo2
        ? accentsTidy(data.deliveryInfo.additionalInfo2)
        : undefined,
      city: accentsTidy(data.deliveryInfo.city),
      stateOrProvinceCode: data.deliveryInfo.stateOrProvinceCode,
      postalCode: data.deliveryInfo.postalCode || "",
    },
    shipperInfo,
    manufacturerInfo: mapManufacturerInfo(
      "manufacturerInfo" in data ? data.manufacturerInfo : undefined,
    ),
    billingEntityId: data.billingEntityId,
    pickUpLocationId: data.pickUpLocationId,
    reference: data.reference ? accentsTidy(data.reference) : undefined,
    options: {
      ...options,
      remotePickup: shipperInfo.companyName === "Pelican Air Services" ? false : true,
      sampleText: data.options.sample ? data.options.sampleText : "",
      daeNumbers: data.options.dae ? data.options.daeNumbers : [],
      mrn: data.options.dae && data.options.mrn,
      mrnNumbers: data.options.dae && data.options.mrn ? data.options.mrnNumbers : [],
      handDelivery: data.apiEnum === "NONE" && data.options.handDelivery,
    },
    importerId: data.importerId || undefined,
    ...typeDetails,
  };
};

export const preFinalizeCustomQuotationPayload = ({
  data,
}: {
  data: Extract<CustomQuotationUpdateBoFormData, { apiEnum: "NONE" }>;
}): PreFinalizeCustomQuotationApiDto => {
  const basePayload = preFinalizeCustomQuotationPayloadBase({ data });

  const expeditionWoApi: PreFinalizeCustomQuotationApiDto["expeditionWoApi"] = {
    packageCount: data.expeditionWoApi.packageCount,
    weight: data.expeditionWoApi.weight,
  };

  const deliveryContent: PreFinalizeCustomQuotationApiDto["deliveryContent"] = mapDeliveryContent(
    data.deliveryContent,
  );

  return {
    ...basePayload,
    expeditionWoApi,
    deliveryContent,
  };
};

export const preFinalizeCustomQuotationFedexPayload = ({
  data,
}: {
  data: Extract<CustomQuotationUpdateBoFormData, { apiEnum: "FEDEX" }>;
}): PreFinalizeCustomQuotationFedexApiDto => {
  const basePayload = preFinalizeCustomQuotationPayloadBase({ data });

  const packaging: PreFinalizeCustomQuotationFedexApiDto["expeditionFedexApi"]["packaging"] =
    data.expeditionFedexApi.expeditionPackaging.map((packaging) => ({
      count: packaging.count,
      weight: packaging.weight,
      length: packaging.length,
      width: packaging.width,
      height: packaging.height,
      value: data.options?.insurance ? packaging.value : 0,
    }));

  const expeditionFedexApi: PreFinalizeCustomQuotationFedexApiDto["expeditionFedexApi"] = {
    deliveryInstructions: data.expeditionFedexApi.deliveryInstructions ?? undefined,
    serviceType: data.expeditionFedexApi.serviceType as FedexServiceEnum,
    packagingType: data.expeditionFedexApi.packagingType as FedexPackagingEnum,
    signatureType: data.expeditionFedexApi.signatureType as FedexSignatureEnum,
    packaging,
    shippingReference: data.expeditionFedexApi.shippingReference ?? undefined,
    fedexAccountId: data.expeditionFedexApi.fedexAccountId,
    shipDate: format(data.expeditionFedexApi.shipDate, "yyyy-MM-dd"),
    fedexRate: data.expeditionFedexApi.fedexRate,
  };

  const deliveryContent: PreFinalizeCustomQuotationApiDto["deliveryContent"] = mapDeliveryContent(
    data.deliveryContent,
  );

  return {
    ...basePayload,
    expeditionFedexApi,
    deliveryContent,
  };
};

export const preFinalizeFedexQuotationPayload = ({
  data,
}: {
  data: Extract<QuotationUpdateBoFormData, { apiEnum: "FEDEX" }>;
}): PreFinalizeQuotationFedexApiDto => {
  const basePayload = preFinalizeQuotationPayload({ data });

  const packaging: PreFinalizeQuotationFedexApiDto["expeditionFedexApi"]["packaging"] =
    data.expeditionFedexApi.expeditionPackaging.map((packaging) => ({
      count: packaging.count,
      weight: packaging.weight,
      length: packaging.length,
      width: packaging.width,
      height: packaging.height,
      value: data.options?.insurance ? packaging.value : 0,
    }));

  const expeditionFedexApi: PreFinalizeQuotationFedexApiDto["expeditionFedexApi"] = {
    deliveryInstructions: data.expeditionFedexApi.deliveryInstructions ?? undefined,
    serviceType: data.expeditionFedexApi.serviceType as FedexServiceEnum,
    packagingType: data.expeditionFedexApi.packagingType as FedexPackagingEnum,
    signatureType: data.expeditionFedexApi.signatureType as FedexSignatureEnum,
    packaging,
    shippingReference: data.expeditionFedexApi.shippingReference ?? undefined,
    fedexAccountId: data.expeditionFedexApi.fedexAccountId,
    shipDate: format(data.expeditionFedexApi.shipDate, "yyyy-MM-dd"),
    fedexRate: data.expeditionFedexApi.fedexRate,
  };

  return {
    ...basePayload,
    expeditionFedexApi,
  };
};

export const preFinalizeCustomQuotationSimplifiedPayload = ({
  data,
}: {
  data: Extract<CustomQuotationUpdateBoFormData, { apiEnum: "NONE_SIMPLIFIED" }>;
}): PreFinalizeCustomQuotationSimplifiedApiDto => {
  const basePayload = preFinalizeCustomQuotationPayloadBase({ data });
  return {
    ...basePayload,
  };
};

const updatePreFinalizeQuotationPayload = ({
  data,
  note,
}: {
  data: QuotationUpdateBoFormData;
  note?: string;
}): UpdatePreFinalizeQuotationApiDto => {
  const deliveryContent: UpdatePreFinalizeQuotationApiDto["deliveryContent"] = mapDeliveryContent(
    data.deliveryContent,
  );

  const shipperInfo: UpdatePreFinalizeQuotationApiDto["shipperInfo"] = {
    contactName: data.shipperInfo.contactName
      ? accentsTidy(data.shipperInfo.contactName)
      : undefined,
    companyName: data.shipperInfo.companyName
      ? accentsTidy(data.shipperInfo.companyName)
      : undefined,
    streetLines: data.shipperInfo.streetLines
      ? data.shipperInfo.streetLines.map((line) => accentsTidy(line))
      : [],
    city: data.shipperInfo.city ? accentsTidy(data.shipperInfo.city) : undefined,
    stateOrProvinceCode: data.shipperInfo.stateOrProvinceCode || undefined,
    postalCode: data.shipperInfo.postalCode || undefined,
    countryCode: data.shipperInfo.countryCode || undefined,
    email: data.shipperInfo.email || undefined,
    phone: data.shipperInfo.phone || undefined,
    residential: data.shipperInfo.residential,
    taxId: data.shipperInfo.taxId || undefined,
  };

  const { presonalUseToggle: _presonalUseToggle, ...options } = data.options;

  return {
    deliveryInfo: {
      ...data.deliveryInfo,
      company: data.deliveryInfo.company ? accentsTidy(data.deliveryInfo.company) : undefined,
      lastName: data.deliveryInfo.lastName ? accentsTidy(data.deliveryInfo.lastName) : undefined,
      firstName: data.deliveryInfo.firstName ? accentsTidy(data.deliveryInfo.firstName) : undefined,
      street: data.deliveryInfo.street ? accentsTidy(data.deliveryInfo.street) : undefined,
      additionalInfo: data.deliveryInfo.additionalInfo
        ? accentsTidy(data.deliveryInfo.additionalInfo)
        : undefined,
      additionalInfo2: data.deliveryInfo.additionalInfo2
        ? accentsTidy(data.deliveryInfo.additionalInfo2)
        : undefined,
      city: data.deliveryInfo.city ? accentsTidy(data.deliveryInfo.city) : undefined,
      stateOrProvinceCode: data.deliveryInfo.stateOrProvinceCode,
      postalCode: data.deliveryInfo.postalCode || "",
    },
    shipperInfo,
    manufacturerInfo: mapManufacturerInfo(
      "manufacturerInfo" in data ? data.manufacturerInfo : undefined,
    ),
    customId: data.customId || undefined,
    deliveryContent,
    woodBoxes: data.quotationWoodBox,
    billingEntityId: data.billingEntityId || undefined,
    pickUpLocationId: data.pickUpLocationId || undefined,
    reference: data.reference ? accentsTidy(data.reference) : undefined,
    options: {
      ...options,
      sampleText: data.options.sample ? data.options.sampleText : undefined,
      daeNumbers: data.options.dae ? data.options.daeNumbers : undefined,
      mrn: data.options.dae && data.options.mrn,
      mrnNumbers: data.options.dae && data.options.mrn ? data.options.mrnNumbers : undefined,
      handDelivery: data.apiEnum === "NONE" && data.options.handDelivery,
    },
    updatePriceParameters: data.updatePriceParameters,
    importerId: data.importerId || undefined,
    note,
  };
};

export const updatePreFinalizeWoApiQuotationPayload = ({
  data,
  note,
}: {
  data: Extract<QuotationUpdateBoFormData, { apiEnum: "NONE" }>;
  note?: string;
}): UpdatePreFinalizeQuotationApiDto => {
  const basePayload = updatePreFinalizeQuotationPayload({ data, note });

  const expeditionWoApi: UpdatePreFinalizeQuotationApiDto["expeditionWoApi"] = {
    packageCount: data.expeditionWoApi.packageCount,
    weight: data.expeditionWoApi.weight,
  };

  return {
    ...basePayload,
    expeditionWoApi,
  };
};

export const UpdateFinalizedQuotationDefaultValues = ({
  expedition,
  company,
  apiEnum,
}: {
  expedition: GetOneExpeditionBoResponseDto;
  company: GetAllCompanyDto;
  apiEnum: FinalizeApi;
}): UpdateFinalizedQuotationFormData => {
  const baseValues = QuotationUpdateBoDefaultValues({ expedition, company, apiEnum });

  const finalizeDetails = {
    packagingInstructions:
      expedition.expeditionNotes?.find((note) => note.type === NoteType.PACKAGING_INSTRUCTION)
        ?.note ?? "",
    packagingPriority: expedition.expeditionOptions?.packagingPriority ?? false,
  };

  if (apiEnum === "FEDEX") {
    return {
      deliveryInfo: baseValues.deliveryInfo,
      shipperInfo: baseValues.shipperInfo,
      customId: baseValues.customId,
      deliveryContent: baseValues.deliveryContent,
      quotationWoodBox: baseValues.quotationWoodBox,
      billingEntityId: baseValues.billingEntityId,
      pickUpLocationId: baseValues.pickUpLocationId,
      comment: baseValues.comment,
      reference: baseValues.reference,
      updatePriceParameters: baseValues.updatePriceParameters,
      options: baseValues.options,
      importerId: baseValues.importerId,
      apiEnum: "FEDEX",
      ...finalizeDetails,
    };
  }

  return {
    deliveryInfo: baseValues.deliveryInfo,
    shipperInfo: baseValues.shipperInfo,
    customId: baseValues.customId,
    deliveryContent: baseValues.deliveryContent,
    quotationWoodBox: baseValues.quotationWoodBox,
    billingEntityId: baseValues.billingEntityId,
    pickUpLocationId: baseValues.pickUpLocationId,
    comment: baseValues.comment,
    reference: baseValues.reference,
    updatePriceParameters: baseValues.updatePriceParameters,
    options: baseValues.options,
    importerId: baseValues.importerId,
    apiEnum: "NONE",
    trackingUrl: expedition.tracking?.trackingUrl ?? "",
    trackingCode: expedition.tracking?.trackingCode ?? "",
    ...finalizeDetails,
  };
};

export const updateFinalizedQuotationPayload = ({
  data,
  expedition,
}: {
  data: UpdateFinalizedQuotationFormData;
  expedition: GetOneExpeditionBoResponseDto;
}): UpdateFinalizedQuotationDto => {
  const basePayload = preFinalizeQuotationPayload({
    data: data as unknown as QuotationUpdateBoFormData,
  });

  return {
    ...basePayload,
    apiEnum: data.apiEnum,
    options: {
      ...basePayload.options,
      packagingPriority: data.packagingPriority,
    },
    expeditionWoApi:
      data.apiEnum === "NONE" && expedition.expeditionWoApi
        ? {
            packageCount: expedition.expeditionWoApi.packageCount,
            weight: expedition.expeditionWoApi.weight,
          }
        : null,
    trackingUrl: data.apiEnum === "NONE" ? data.trackingUrl || undefined : undefined,
    trackingCode: data.apiEnum === "NONE" ? data.trackingCode || undefined : undefined,
    packagingInstructions: data.packagingInstructions || undefined,
    packagingPriority: data.packagingPriority,
  };
};

export const UpdateFinalizedCustomQuotationDefaultValues = ({
  expedition,
  company,
  apiEnum,
}: {
  expedition: GetOneExpeditionBoResponseDto;
  company: GetAllCompanyDto;
  apiEnum: FinalizeApi;
}): UpdateFinalizedCustomQuotationFormData => {
  const baseValues = CustomQuotationUpdateBoDefaultValues({ expedition, company, apiEnum });
  const deliveryContent =
    "deliveryContent" in baseValues && baseValues.deliveryContent ? baseValues.deliveryContent : [];

  const sharedFields = {
    deliveryInfo: baseValues.deliveryInfo,
    shipperInfo: baseValues.shipperInfo,
    customId: baseValues.customId,
    billingEntityId: baseValues.billingEntityId,
    pickUpLocationId: baseValues.pickUpLocationId,
    comment: baseValues.comment,
    reference: baseValues.reference,
    options: baseValues.options,
    importerId: baseValues.importerId,
    type: baseValues.type,
    parcelDetail: baseValues.parcelDetail,
    paletteDetail: baseValues.paletteDetail,
    otherDetail: baseValues.otherDetail,
    packagingInstructions:
      expedition.expeditionNotes?.find((note) => note.type === NoteType.PACKAGING_INSTRUCTION)
        ?.note ?? "",
    packagingPriority: expedition.expeditionOptions?.packagingPriority ?? false,
  };

  if (apiEnum === "FEDEX") {
    return {
      ...sharedFields,
      deliveryContent,
      apiEnum: "FEDEX",
    };
  }

  if (apiEnum === "NONE_SIMPLIFIED") {
    return {
      ...sharedFields,
      apiEnum: "NONE_SIMPLIFIED",
      trackingUrl: expedition.tracking?.trackingUrl ?? "",
      trackingCode: expedition.tracking?.trackingCode ?? "",
    };
  }

  return {
    ...sharedFields,
    deliveryContent,
    apiEnum: "NONE",
    trackingUrl: expedition.tracking?.trackingUrl ?? "",
    trackingCode: expedition.tracking?.trackingCode ?? "",
  };
};

export const updateFinalizedCustomQuotationPayload = ({
  data,
  expedition,
}: {
  data: UpdateFinalizedCustomQuotationFormData;
  expedition: GetOneExpeditionBoResponseDto;
}): UpdateFinalizedCustomQuotationDto => {
  const basePayload = preFinalizeCustomQuotationPayloadBase({
    data: data as unknown as CustomQuotationUpdateBoFormData,
  });

  const payload: UpdateFinalizedCustomQuotationDto = {
    ...basePayload,
    apiEnum: data.apiEnum,
    options: {
      ...basePayload.options,
      packagingPriority: data.packagingPriority,
    },
    expeditionWoApi:
      data.apiEnum === "NONE" && expedition.expeditionWoApi
        ? {
            packageCount: expedition.expeditionWoApi.packageCount,
            weight: expedition.expeditionWoApi.weight,
          }
        : null,
    packagingInstructions: data.packagingInstructions || undefined,
    packagingPriority: data.packagingPriority,
  };

  if (data.apiEnum === "NONE_SIMPLIFIED") {
    payload.trackingUrl = data.trackingUrl || undefined;
    payload.trackingCode = data.trackingCode || undefined;
  } else if (data.apiEnum === "NONE") {
    payload.deliveryContent = mapDeliveryContent(data.deliveryContent);
    payload.trackingUrl = data.trackingUrl || undefined;
    payload.trackingCode = data.trackingCode || undefined;
  } else {
    payload.deliveryContent = mapDeliveryContent(data.deliveryContent);
  }

  return payload;
};

export const updatePreFinalizeFedexQuotationPayload = ({
  data,
  note,
}: {
  data: Extract<QuotationUpdateBoFormData, { apiEnum: "FEDEX" }>;
  note?: string;
}): UpdatePreFinalizeQuotationApiDto => {
  const basePayload = updatePreFinalizeQuotationPayload({ data, note });

  const packaging: NonNullable<
    UpdatePreFinalizeQuotationApiDto["expeditionFedexApi"]
  >["packaging"] = data.expeditionFedexApi.expeditionPackaging.map((packaging) => ({
    count: packaging.count,
    weight: packaging.weight,
    length: packaging.length,
    width: packaging.width,
    height: packaging.height,
    value: packaging.value,
  }));

  const expeditionFedexApi: UpdatePreFinalizeQuotationApiDto["expeditionFedexApi"] = {
    deliveryInstructions: data.expeditionFedexApi.deliveryInstructions || undefined,
    serviceType: data.expeditionFedexApi.serviceType as FedexServiceEnum | undefined,
    packagingType: data.expeditionFedexApi.packagingType as FedexPackagingEnum | undefined,
    signatureType: data.expeditionFedexApi.signatureType as FedexSignatureEnum | undefined,
    packaging,
    shippingReference: data.expeditionFedexApi.shippingReference || undefined,
    fedexAccountId: data.expeditionFedexApi.fedexAccountId || undefined,
    shipDate: data.expeditionFedexApi.shipDate
      ? format(data.expeditionFedexApi.shipDate, "yyyy-MM-dd")
      : undefined,
    fedexRate: data.expeditionFedexApi.fedexRate,
  };

  return {
    ...basePayload,
    expeditionFedexApi,
  };
};

const updatePreFinalizeCustomQuotationPayloadBase = ({
  data,
  note,
}: {
  data: CustomQuotationUpdateBoFormData;
  note?: string;
}): UpdatePreFinalizeCustomQuotationApiDto => {
  const shipperInfo: UpdatePreFinalizeCustomQuotationApiDto["shipperInfo"] = {
    contactName: data.shipperInfo.contactName
      ? accentsTidy(data.shipperInfo.contactName)
      : undefined,
    companyName: data.shipperInfo.companyName
      ? accentsTidy(data.shipperInfo.companyName)
      : undefined,
    streetLines: data.shipperInfo.streetLines
      ? data.shipperInfo.streetLines.map((line) => accentsTidy(line))
      : [],
    city: data.shipperInfo.city ? accentsTidy(data.shipperInfo.city) : undefined,
    stateOrProvinceCode: data.shipperInfo.stateOrProvinceCode || undefined,
    postalCode: data.shipperInfo.postalCode || undefined,
    countryCode: data.shipperInfo.countryCode || undefined,
    email: data.shipperInfo.email || undefined,
    phone: data.shipperInfo.phone || undefined,
    residential: data.shipperInfo.residential,
    taxId: data.shipperInfo.taxId || undefined,
  };

  const { presonalUseToggle: _presonalUseToggle, ...options } = data.options;

  const typeDetails = mapCustomQuotationTypeDetails(data);

  return {
    deliveryInfo: {
      ...data.deliveryInfo,
      company: data.deliveryInfo.company ? accentsTidy(data.deliveryInfo.company) : undefined,
      lastName: data.deliveryInfo.lastName ? accentsTidy(data.deliveryInfo.lastName) : undefined,
      firstName: data.deliveryInfo.firstName ? accentsTidy(data.deliveryInfo.firstName) : undefined,
      street: data.deliveryInfo.street ? accentsTidy(data.deliveryInfo.street) : undefined,
      additionalInfo: data.deliveryInfo.additionalInfo
        ? accentsTidy(data.deliveryInfo.additionalInfo)
        : undefined,
      additionalInfo2: data.deliveryInfo.additionalInfo2
        ? accentsTidy(data.deliveryInfo.additionalInfo2)
        : undefined,
      city: data.deliveryInfo.city ? accentsTidy(data.deliveryInfo.city) : undefined,
    },
    shipperInfo,
    manufacturerInfo: mapManufacturerInfo(
      "manufacturerInfo" in data ? data.manufacturerInfo : undefined,
    ),
    billingEntityId: data.billingEntityId || undefined,
    pickUpLocationId: data.pickUpLocationId || undefined,
    reference: data.reference ? accentsTidy(data.reference) : undefined,
    options: {
      ...options,
      sampleText: data.options.sample ? data.options.sampleText : undefined,
      daeNumbers: data.options.dae ? data.options.daeNumbers : undefined,
      mrn: data.options.dae && data.options.mrn,
      mrnNumbers: data.options.dae && data.options.mrn ? data.options.mrnNumbers : undefined,
      handDelivery: data.apiEnum === "NONE" && data.options.handDelivery,
    },
    importerId: data.importerId || undefined,
    note,
    ...typeDetails,
  };
};

export const updatePreFinalizeCustomQuotationPayload = ({
  data,
  note,
}: {
  data: Extract<CustomQuotationUpdateBoFormData, { apiEnum: "NONE" }>;
  note?: string;
}): UpdatePreFinalizeCustomQuotationApiDto => {
  const basePayload = updatePreFinalizeCustomQuotationPayloadBase({ data, note });

  const expeditionWoApi: UpdatePreFinalizeCustomQuotationApiDto["expeditionWoApi"] = {
    packageCount: data.expeditionWoApi.packageCount,
    weight: data.expeditionWoApi.weight,
  };

  const deliveryContent: UpdatePreFinalizeCustomQuotationApiDto["deliveryContent"] =
    mapDeliveryContent(data.deliveryContent);

  return {
    ...basePayload,
    expeditionWoApi,
    deliveryContent,
  };
};

export const updatePreFinalizeCustomQuotationFedexPayload = ({
  data,
  note,
}: {
  data: Extract<CustomQuotationUpdateBoFormData, { apiEnum: "FEDEX" }>;
  note?: string;
}): UpdatePreFinalizeCustomQuotationApiDto => {
  const basePayload = updatePreFinalizeCustomQuotationPayloadBase({ data, note });

  const packaging: NonNullable<
    UpdatePreFinalizeCustomQuotationApiDto["expeditionFedexApi"]
  >["packaging"] = data.expeditionFedexApi.expeditionPackaging.map((packaging) => ({
    count: packaging.count,
    weight: packaging.weight,
    length: packaging.length,
    width: packaging.width,
    height: packaging.height,
    value: packaging.value,
  }));

  const expeditionFedexApi: UpdatePreFinalizeCustomQuotationApiDto["expeditionFedexApi"] = {
    deliveryInstructions: data.expeditionFedexApi.deliveryInstructions || undefined,
    serviceType: data.expeditionFedexApi.serviceType as FedexServiceEnum | undefined,
    packagingType: data.expeditionFedexApi.packagingType as FedexPackagingEnum | undefined,
    signatureType: data.expeditionFedexApi.signatureType as FedexSignatureEnum | undefined,
    packaging,
    shippingReference: data.expeditionFedexApi.shippingReference || undefined,
    fedexAccountId: data.expeditionFedexApi.fedexAccountId || undefined,
    shipDate: data.expeditionFedexApi.shipDate
      ? format(data.expeditionFedexApi.shipDate, "yyyy-MM-dd")
      : undefined,
    fedexRate: data.expeditionFedexApi.fedexRate,
  };

  const deliveryContent: UpdatePreFinalizeCustomQuotationApiDto["deliveryContent"] =
    mapDeliveryContent(data.deliveryContent);

  return {
    ...basePayload,
    expeditionFedexApi,
    deliveryContent,
  };
};

export const updatePreFinalizeCustomQuotationSimplifiedPayload = ({
  data,
  note,
}: {
  data: Extract<CustomQuotationUpdateBoFormData, { apiEnum: "NONE_SIMPLIFIED" }>;
  note?: string;
}): UpdatePreFinalizeCustomQuotationApiDto => {
  const basePayload = updatePreFinalizeCustomQuotationPayloadBase({ data, note });

  return {
    ...basePayload,
  };
};
